import { mergeMap } from 'rxjs/operators';
import { Component, OnInit, OnDestroy, Input, TemplateRef, ContentChild, inject } from '@angular/core';
import { LimitOffsetPaging } from 'app/core/models/paged-results/limit-offset-paging.model';
import { UntypedFormBuilder, FormsModule } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { ReportsService } from 'app/core/services/reports.service';
import { verifiedStatuses } from 'app/core/options/capture-status.opts';
import { GenericCountService } from 'app/core/services/generic-count.service';
import { DownloadService } from 'app/core/services/download.service';
import { Report, AppliedReportOptions } from 'app/core/models/report.model';
import { NewReportOptionsTemplateDirective } from 'app/shared/directives/new-report-options-template.directive';
import { ReportDetailsTemplateDirective } from 'app/shared/directives/report-details-template.directive';
import { interval } from 'rxjs';
import { NgTemplateOutlet, DecimalPipe, TitleCasePipe, DatePipe } from '@angular/common';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { PaginationHeaderComponent } from '../pagination-header/pagination-header.component';
import { PaginationComponent } from '../pagination/pagination.component';
import { MatProgressSpinner } from '@angular/material/progress-spinner';

@Component({
    selector: 'app-reports',
    templateUrl: './reports.component.html',
    styleUrls: ['./reports.component.scss'],
    imports: [FormsModule,
              NgTemplateOutlet,
              FaIconComponent,
              PaginationHeaderComponent,
              PaginationComponent,
              MatProgressSpinner,
              DecimalPipe,
              TitleCasePipe,
              DatePipe]
})
export class ReportsComponent implements OnInit, OnDestroy {
  private reportsService = inject(ReportsService);
  private downloadService = inject(DownloadService);
  private genericCountService = inject(GenericCountService);
  private formBuilder = inject(UntypedFormBuilder);

  static readonly activePollingInterval = 5000;

  paging = LimitOffsetPaging.empty;
  availableRecordCount: number = null;
  loading = true;
  activePolling = new Map<number, object>();

  reports: Report[];
  reportIndex = new Map<number, number>();

  creationError: string;

  @Input() filtersForm = this.formBuilder.group({});
  @Input() outputOptionsForm = this.formBuilder.group({});
  @Input() optionSerializationParams = { filters: {}, outputOptions: {} };
  @Input() validateForm: () => boolean;
  @Input() formMessage: string;

  @Input() reportsUrl: string;
  @Input() reportsCountUrl: string;
  @Input() title: string;
  @Input() displayProcessedColumn = true;
  @Input() totalColumnHeader = 'Total';
  @Input() forceReportDetailsDisplay = false;

  @ContentChild(NewReportOptionsTemplateDirective, { read: TemplateRef })
  newReportOptionsTemplate: TemplateRef<any>;
  @ContentChild(ReportDetailsTemplateDirective, { read: TemplateRef })
  reportDetailsTemplate: TemplateRef<any>;

  ngOnInit() {
    this.loadReports(1);
  }

  onPageSizeChange(size: number) {
    this.loadReports(this.paging.currentPage, size);
  }

  onPageChange(page: number) {
    this.loadReports(page);
  }

  ngOnDestroy() {
    this.stopAllActivePolling();
  }

  get newReportOptionsValid(): boolean {
    return this.filtersForm.valid &&
      this.outputOptionsForm.valid &&
      (!this.validateForm || this.validateForm());
  }

  get generating(): boolean {
    return this.reports.some(report => this.isProcessing(report));
  }

  generate() {
    delete this.creationError;

    const filters = this.filterParams();

    const includesAllVerifiedStatuses = filters.statusAndReasons?.verified_all_statuses

    if (includesAllVerifiedStatuses) {
      const { statusAndReasons } = filters;
      delete statusAndReasons.verified_all_statuses;
      verifiedStatuses.forEach(status => {
        statusAndReasons[status] = [];
      });
    }

    this.reportsService.create(this.reportsUrl, filters, this.outputOptionParams()).subscribe(
      report => {
        this.setReports([report, ...this.reports]);
        this.startActivePolling(report.id);
      },
      (response: HttpErrorResponse) => {
        if (response.error && response.error.error) {
          this.creationError = response.error.error;
        }
      }
    );
  }

  loadReports(page: number, pageSize: number = this.paging.pageSize) {
    this.loading = true;
    this.reportsService.getList(this.reportsUrl, { page, pageSize }).subscribe(result => {
      this.setReports(result.reports);
      this.paging = result.meta.paging;
      this.loading = false;
      this.resetActivePolling();
    });
  }

  download(report: Report) {
    this.downloadService.getDownloadUrl(report.reportFileExpirableDownloadPath).subscribe(expirableDownload => {
      this.downloadService.download(expirableDownload.url);
    });
  }

  cancel(report: Report) {
    this.reportsService.cancel(this.reportsUrl, report.id).subscribe(foundReport => {
      this.replaceReport(foundReport);
    });
  }

  isProcessing(report: Report) {
    return report.status === 'processing' || report.status === 'initialized';
  }

  private optionsSetAndNotEmpty(options: AppliedReportOptions) {
    return (
      options &&
      Object.values(options).some(value => value && (!Array.isArray(value) || value.length > 0))
    );
  }

  hasAppliedOptions(report: Report) {
    return this.optionsSetAndNotEmpty(report.filters) || this.optionsSetAndNotEmpty(report.outputOptions);
  }

  private initiateActivePolling() {
    const inProcess = this.reports.filter(report => report.status === 'initialized' || report.status === 'processing');
    inProcess.forEach(report => {
      this.startActivePolling(report.id);
    });
  }

  private startActivePolling(reportId: number) {
    if (!this.activePolling[reportId]) {
      this.activePolling[reportId] = interval(ReportsComponent.activePollingInterval)
        .pipe(mergeMap(() => this.reportsService.get(this.reportsUrl, reportId)))
        .subscribe(report => {
          this.replaceReport(report);
        });
    }
  }

  private resetActivePolling() {
    this.stopAllActivePolling();
    this.initiateActivePolling();
  }

  private stopAllActivePolling() {
    this.activePolling.forEach((_, reportId) => {
      this.stopActivePolling(reportId);
    });
    this.activePolling = new Map<number, object>();
  }

  private stopActivePolling(reportId: number) {
    if (this.activePolling[reportId]) {
      this.activePolling[reportId].unsubscribe();
      this.activePolling[reportId] = null;
      this.activePolling.delete(reportId);
    }
  }

  private setReports(reports: Report[]) {
    this.reports = reports;
    this.resetReportIndex();

    if (this.reports.length === 0 && this.reportsCountUrl && this.availableRecordCount === null) {
      this.genericCountService.getCount(this.reportsCountUrl).subscribe(data => {
        this.availableRecordCount = data.count;
      });
    }
  }

  private resetReportIndex() {
    this.reportIndex = new Map<number, number>();
    let index = 0;
    this.reports.forEach(report => {
      this.reportIndex[report.id] = index;
      index += 1;
    });
  }

  private replaceReport(report: Report) {
    const index = this.reportIndex[report.id];
    this.reports[index] = report;
  }

  private optionParams(options: AppliedReportOptions, optionType: string) {
    return Object.keys(options).reduce((result, optionName) => {
      const filter = options[optionName];

      if (filter) {
        const serializationParams = this.optionSerializationParams[optionType][optionName] || {};

        if (!serializationParams.paramName) {
          serializationParams.paramName = optionName;
        }

        const filterParams = filter.toPostData(serializationParams);
        return { ...result, ...filterParams };
      } else {
        return result;
      }
    }, {});
  }

  private filterParams() {
    const filters = this.filtersForm.value;

    return this.optionParams(filters, 'filters');
  }

  private outputOptionParams() {
    const outputOptions = this.outputOptionsForm.value;

    return this.optionParams(outputOptions, 'outputOptions');
  }
}
